import React, { useState, useEffect } from 'react';
import FixedUploadDialog from './FixedUploadDialog';
import FileBrowser from './FileBrowser';

interface FileManagerSectionProps {
  selectedBucket: string;
  s3Client: any;
  permissions: {
    list: boolean;
    download: boolean;
    upload: boolean;
    delete: boolean;
    maxObjectSize: number;
    monthlyDataTransferCap: number;
  };
  refreshTrigger: number;
  onUploadComplete: () => void;
  initialFolderPath?: string; // For folder-level permission restrictions
}

const FileManagerSection: React.FC<FileManagerSectionProps> = ({
  selectedBucket,
  s3Client,
  permissions,
  refreshTrigger,
  onUploadComplete,
  initialFolderPath
}) => {
  const [currentPrefix, setCurrentPrefix] = useState(initialFolderPath || '');

  // Reset prefix when bucket or initialFolderPath changes
  useEffect(() => {
    setCurrentPrefix(initialFolderPath || '');
  }, [selectedBucket, initialFolderPath]);

  const handlePrefixChange = (newPrefix: string) => {
    setCurrentPrefix(newPrefix);
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900">
                Files in {selectedBucket}
                {currentPrefix && ` / ${currentPrefix.replace(/\/$/, '')}`}
              </h2>
            </div>
            <div className="text-sm text-gray-500">
              {permissions.list ? 'Browse enabled' : 'Limited access'}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Fixed Upload Dialog - Uses current folder prefix */}
          <FixedUploadDialog
            bucketName={selectedBucket}
            prefix={currentPrefix}
            onUploadComplete={onUploadComplete}
            permissions={permissions}
          />

          {/* File Browser - Reports current folder changes */}
          <FileBrowser
            bucketName={selectedBucket}
            s3Client={s3Client}
            permissions={permissions}
            refreshTrigger={refreshTrigger}
            onPrefixChange={handlePrefixChange}
            initialFolderPath={initialFolderPath}
          />
        </div>
      </div>
    </div>
  );
};

export default FileManagerSection;